import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  Divider
} from '@mui/material';
import CalendarHeatmap from 'react-calendar-heatmap';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-calendar-heatmap/dist/styles.css';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import { envisFindingsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Label configuration per featured type
const HEATMAP_CONFIG = {
  potholes: {
    title: 'Pothole Occurrence Heatmap',
    subtitle: (year) => `${year} validated pothole patterns`,
    filterKey: 'pothole',
    loadingText: 'Loading validated pothole data...',
    errorText: 'Failed to load validated pothole data',
    tooltipSingular: 'pothole',
    tooltipPlural: 'potholes',
    noDataText: 'No potholes detected',
    cardColor: (theme) => theme.palette.error.main,
    statLabel: 'active days',
  },
  foreign_object: {
    title: 'Foreign Object Occurrence Heatmap',
    subtitle: (year) => `${year} validated foreign object patterns`,
    filterKey: 'foreign object',
    loadingText: 'Loading validated foreign object data...',
    errorText: 'Failed to load validated foreign object data',
    tooltipSingular: 'foreign object',
    tooltipPlural: 'foreign objects',
    noDataText: 'No foreign objects detected',
    cardColor: (theme) => theme.palette.warning.dark,
    statLabel: 'active days',
  },
};

const PotholeHeatmapSection = ({ user, featuredType = 'potholes' }) => {
  const theme = useTheme();
  const { isAuthenticated, getUserOrganization } = useAuth();
  const config = HEATMAP_CONFIG[featuredType] || HEATMAP_CONFIG.potholes;

  // State for data and loading
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get organization info
  const userOrganization = getUserOrganization();
  const organizationId = userOrganization?.id;

  // Fetch data from API based on featuredType
  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated || !organizationId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Calculate date range for the current calendar year
        const currentYear = new Date().getFullYear();
        const startDate = new Date(currentYear, 0, 1);
        const endDate = new Date(currentYear, 11, 31);

        // Fetch validated findings of the selected type for the current year
        let params = {
          from_date: startDate.toISOString().split('T')[0],
          to_date: endDate.toISOString().split('T')[0],
          limit: 10000,
          offset: 0,
          sort_by: 'first_detected_at',
          sort_order: 'DESC',
          validation_status: 'validated',
        };

        // Apply defect type filter server-side
        if (featuredType === 'foreign_object') {
          params.is_foreign_object = 'true';
        } else {
          params.is_pothole = 'true';
        }

        // 🔐 ADD PERMISSION-BASED FILTERING (same pattern as DefectsPage)
        if (user?.permissionScope === 'network_section') {
          const accessibleNetworkIds = user.accessible_networks || [];
          const accessibleSectionIds = user.accessible_sections || [];

          if (accessibleNetworkIds.length > 0) {
            params.network_ids = accessibleNetworkIds.join(',');
          }

          if (accessibleSectionIds.length > 0) {
            params.section_ids = accessibleSectionIds.join(',');
          }

          const hasNetworkAccess = accessibleNetworkIds.length > 0;
          const hasSectionAccess = accessibleSectionIds.length > 0;

          if (!hasNetworkAccess && !hasSectionAccess) {
            console.warn(`[HeatmapSection] User ${user.username} has no network or section permissions`);
            setRawData([]);
            setLoading(false);
            return;
          }
        }

        const response = await envisFindingsApi.getTableFindings(params);
        setRawData(response.data.data || []);
      } catch (err) {
        console.error(`Error fetching ${featuredType} data:`, err);
        setError(config.errorText);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, organizationId, user, featuredType, config.errorText]);

  // Process raw data into heatmap format
  const heatmapData = useMemo(() => {
    // Create a complete calendar year of dates with zero counts
    const yearData = [];
    const currentYear = new Date().getFullYear();
    const startDate = new Date(currentYear, 0, 1); // January 1st
    const endDate = new Date(currentYear, 11, 31); // December 31st
    
    // Calculate total days in the current year (handles leap years)
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    
    for (let i = 0; i < totalDays; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      yearData.push({
        date: date.toISOString().split('T')[0],
        count: 0
      });
    }

    // If we have raw data, update the counts
    if (rawData.length > 0) {
      const dateGroups = {};
      
      rawData.forEach(item => {
        if (item.first_detected_at) {
          const date = new Date(item.first_detected_at).toISOString().split('T')[0];
          dateGroups[date] = (dateGroups[date] || 0) + 1;
        }
      });

      // Update the year data with actual counts
      yearData.forEach(dayData => {
        if (dateGroups[dayData.date]) {
          dayData.count = dateGroups[dayData.date];
        }
      });
    }

    return yearData;
  }, [rawData]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!heatmapData.length) {
      return {
        total: 0,
        daysWithPotholes: 0,
        avgPerDay: '0.0',
        maxDay: 0,
        streakDays: 0
      };
    }

    const counts = heatmapData.map(d => d.count);
    const total = counts.reduce((sum, count) => sum + count, 0);
    const daysWithPotholes = counts.filter(count => count > 0).length;
    const avgPerDay = total / heatmapData.length; // Use actual days in year
    const maxDay = Math.max(...counts, 0);
    
    // Calculate current streak
    let currentStreak = 0;
    const today = new Date();
    
    const sortedData = [...heatmapData]
      .filter(d => new Date(d.date) <= today)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    
    for (let i = 0; i < sortedData.length; i++) {
      const expectedDate = new Date(today);
      expectedDate.setDate(today.getDate() - i);
      const expectedDateStr = expectedDate.toISOString().split('T')[0];
      
      const dayData = sortedData.find(d => d.date === expectedDateStr);
      if (dayData && dayData.count > 0) {
        currentStreak++;
      } else {
        break;
      }
    }
    
    return {
      total,
      daysWithPotholes,
      avgPerDay: avgPerDay.toFixed(1),
      maxDay,
      streakDays: currentStreak
    };
  }, [heatmapData]);

  // Get start and end dates for the heatmap (current calendar year)
  const currentYear = new Date().getFullYear();
  const startDate = new Date(currentYear, 0, 1); // January 1st
  const endDate = new Date(currentYear, 11, 31); // December 31st

  // Card style consistent with other dashboard components
  const cardStyle = {
    p: 0,
    borderRadius: 2,
    height: 'fit-content',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
    }
  };

  const cardColor = config.cardColor(theme);

  // Show loading state
  if (loading) {
    return (
      <Paper sx={{ ...cardStyle, minHeight: '400px', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            {config.loadingText}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Show error state
  if (error) {
    return (
      <Paper sx={{ ...cardStyle, minHeight: '400px', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Please try refreshing the page
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Show authentication required state
  if (!isAuthenticated || !organizationId) {
    return (
      <Paper sx={{ ...cardStyle, minHeight: '400px', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {!isAuthenticated ? 'Authentication required' : 'No organization access'}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={cardStyle}>
      {/* Header Section - Consistent with DefectSummarySection */}
      <Box 
        sx={{ 
          px: 2, 
          py: 1.5, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: alpha(cardColor, 0.15),
          bgcolor: alpha(cardColor, 0.03)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{ 
              bgcolor: alpha(cardColor, 0.1), 
              color: cardColor,
              width: 32, 
              height: 32,
              mr: 1.5
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {config.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {config.subtitle(new Date().getFullYear())}
            </Typography>
          </Box>
        </Box>
        
        <Chip 
          size="small"
          label={`${stats.total} total`}
          sx={{ 
            height: 20, 
            fontSize: '0.7rem',
            bgcolor: alpha(cardColor, 0.1),
            color: cardColor,
            fontWeight: 600
          }}
        />
      </Box>

      {/* Content Section */}
      <Box sx={{ p: 2 }}>
        {/* Statistics Row */}
        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 0.5 }}>
          <Chip
            icon={<TrendingUpIcon />}
            label={`${stats.avgPerDay}/day avg`}
            variant="outlined"
            size="small"
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
          <Chip
            icon={<TimelineIcon />}
            label={`${stats.daysWithPotholes} active days`}
            variant="outlined"
            size="small"
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
          <Chip
            label={`${stats.maxDay} peak day`}
            variant="outlined"
            size="small"
            color="error"
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        </Stack>

        {/* Calendar Heatmap */}
        <Box sx={{ 
          '& .react-calendar-heatmap': {
            width: '100%',
            height: 'auto'
          },
          '& .react-calendar-heatmap .react-calendar-heatmap-month-label': {
            fontSize: '0.6rem',
            fill: theme.palette.text.secondary,
            fontWeight: 500
          },
          '& .react-calendar-heatmap .react-calendar-heatmap-weekday-label': {
            fontSize: '0.55rem',
            fill: theme.palette.text.secondary
          },
          '& .react-calendar-heatmap rect:hover': {
            stroke: theme.palette.text.primary,
            strokeWidth: 1
          },
          '& .color-empty': {
            fill: alpha(theme.palette.grey[300], 0.3)
          },
          '& .color-github-1': {
            fill: alpha(cardColor, 0.2)
          },
          '& .color-github-2': {
            fill: alpha(cardColor, 0.4)
          },
          '& .color-github-3': {
            fill: alpha(cardColor, 0.6)
          },
          '& .color-github-4': {
            fill: alpha(cardColor, 0.8)
          },
          mb: 1.5
        }}>
          <CalendarHeatmap
            startDate={startDate}
            endDate={endDate}
            values={heatmapData}
            classForValue={(value) => {
              if (!value || value.count === 0) {
                return 'color-empty';
              }
              if (value.count <= 2) return 'color-github-1';
              if (value.count <= 5) return 'color-github-2';
              if (value.count <= 8) return 'color-github-3';
              return 'color-github-4';
            }}
            tooltipDataAttrs={(value) => {
              if (!value || !value.date) {
                return {
                  'data-tooltip-id': 'heatmap-tooltip',
                  'data-tooltip-content': 'No data available'
                };
              }
              const date = new Date(value.date);
              const formattedDate = date.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              });
              const countText = value.count === 0 ? config.noDataText :
                               value.count === 1 ? `1 ${config.tooltipSingular} detected` :
                               `${value.count} ${config.tooltipPlural} detected`;
              return {
                'data-tooltip-id': 'heatmap-tooltip',
                'data-tooltip-content': `${formattedDate} - ${countText}`
              };
            }}
            showWeekdayLabels={true}
            showMonthLabels={true}
            showOutOfRangeDays={false}
          />
          
          {/* React Tooltip */}
          <ReactTooltip
            id="heatmap-tooltip"
            place="top"
            style={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              fontSize: '0.875rem',
              fontWeight: 500,
              padding: '8px 12px',
              zIndex: 9999
            }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontSize: '0.7rem' }}>
              Less
            </Typography>
            {[0, 1, 2, 3, 4].map((intensity) => {
              const getColor = (level) => {
                switch (level) {
                  case 0: return alpha(theme.palette.grey[300], 0.3);
                  case 1: return alpha(cardColor, 0.2);
                  case 2: return alpha(cardColor, 0.4);
                  case 3: return alpha(cardColor, 0.6);
                  case 4: return alpha(cardColor, 0.8);
                  default: return alpha(theme.palette.grey[300], 0.3);
                }
              };
              
              return (
                <Box
                  key={intensity}
                  sx={{
                    width: 10,
                    height: 10,
                    backgroundColor: getColor(intensity),
                    borderRadius: 0.5,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.3)
                  }}
                />
              );
            })}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: '0.7rem' }}>
              More
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Validated ENViS data • Hover for details
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default PotholeHeatmapSection; 